/**
 * Postal-code rules for customer billing addresses.
 *
 * Mirrors the table in the API (`src/non-tenanted/clients/constants/postal-code-rules.ts`) so the
 * portal rejects a bad postal code inline instead of letting the request reach ChargeBee, which
 * answers with `billing_address[zip] : invalid zip/postal code` and used to surface as a 500.
 *
 * Keyed on ALPHA-3, because the country select stores `alphaThreeCode` and mirrors it into
 * `billingInfo.country`. Saudi Arabia is listed under both 'KSA' (the internal identifier held in
 * client_country.alphaThreeCode) and the ISO 'SAU', so the rule applies whichever the API returns.
 * A few alpha-2 codes are included for the same reason, in case a stored value predates the select.
 *
 * DEGRADE PERMISSIVELY. A country with no entry falls back to GENERIC_ZIP_PATTERN, never to a
 * rejection. Omitting a country is harmless; adding a wrong rule blocks a real customer.
 */

/**
 * Country-independent shape check: starts alphanumeric, then alphanumerics, spaces and dashes,
 * 3-13 characters. Catches placeholders and punctuation without assuming a national format.
 */
export const GENERIC_ZIP_PATTERN = /^[A-Za-z0-9][A-Za-z0-9\s-]{2,12}$/;

const FIVE_DIGITS = /^\d{5}$/;

export const POSTAL_CODE_RULES = {
  KSA: FIVE_DIGITS,
  SAU: FIVE_DIGITS,
  SA: FIVE_DIGITS,
  EGY: FIVE_DIGITS,
  JOR: FIVE_DIGITS,
  KWT: FIVE_DIGITS,
  DEU: FIVE_DIGITS,
  FRA: FIVE_DIGITS,
  ESP: FIVE_DIGITS,
  ITA: FIVE_DIGITS,
  PAK: FIVE_DIGITS,
  USA: /^\d{5}(-\d{4})?$/,
  US: /^\d{5}(-\d{4})?$/,
  GBR: /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i,
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i,
  CAN: /^[ABCEGHJ-NPRSTVXY]\d[A-Z][ -]?\d[A-Z]\d$/i,
  CA: /^[ABCEGHJ-NPRSTVXY]\d[A-Z][ -]?\d[A-Z]\d$/i,
  NLD: /^\d{4}\s?[A-Z]{2}$/i,
  IND: /^\d{6}$/,
  AUS: /^\d{4}$/,
};

/**
 * Countries with no national postal-code system. A blank value is legitimate for these and whatever
 * they do supply is a local convention, so format checking is skipped entirely.
 */
export const COUNTRIES_WITHOUT_POSTAL_CODES = ['ARE', 'AE', 'QAT', 'QA', 'HKG', 'HK'];

/**
 * @param {string} [zip] raw postal code; surrounding whitespace is ignored
 * @param {string} [country] country code as held by the form (alpha-3, or alpha-2 for older data)
 * @returns {boolean} false only when the value is definitely wrong for a country we have a rule for
 */
export const isValidPostalCode = (zip, country) => {
  const trimmed = (zip ?? '').trim();
  const code = (country ?? '').trim().toUpperCase();

  if (COUNTRIES_WITHOUT_POSTAL_CODES.includes(code)) return true;

  // A blank value is handled by the required/optional rule on the field itself, not here.
  if (trimmed === '') return true;

  return (POSTAL_CODE_RULES[code] ?? GENERIC_ZIP_PATTERN).test(trimmed);
};
